/**
 * Admin React Query hooks
 *
 * Query and mutation hooks for admin endpoints.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type AcquisitionWindow,
  adminApi,
  type AdminContentDetail,
  type AdminReportFilters,
  type AdminRevealRequest,
  type AdminUserRolesUpdate,
  type AdminUserUpdate,
  type AIUsageDailyWindow,
  type AlertRuleInput,
  type GiftCreditsRequest,
  type HelpQuestionFilters,
  type HelpQuestionStatus,
  type PlanOverrideRequest,
  type ReportStatus,
  type SimulatorEvent,
} from "@/services/admin";

// ── Query key factory ──────────────────────────────────────

export const adminKeys = {
  all: ["admin"] as const,
  dashboard: () => [...adminKeys.all, "dashboard"] as const,
  metrics: (period: string, metric: string) =>
    [...adminKeys.all, "metrics", period, metric] as const,
  users: () => [...adminKeys.all, "users"] as const,
  userList: (params?: {
    search?: string;
    plan?: string;
    status?: string;
    createdFrom?: string;
    createdTo?: string;
    cursor?: string;
  }) => [...adminKeys.users(), "list", params] as const,
  userDetail: (id: string) => [...adminKeys.users(), "detail", id] as const,
  userCredits: (id: string) => [...adminKeys.users(), "credits", id] as const,
  content: () => [...adminKeys.all, "content"] as const,
  contentList: (params?: {
    type?: string;
    q?: string;
    userId?: string;
    status?: string;
    createdFrom?: string;
    createdTo?: string;
    cursor?: string;
  }) => [...adminKeys.content(), "list", params] as const,
  contentDetail: (id: string, params?: { userId?: string; type?: string }) =>
    [...adminKeys.content(), "detail", id, params] as const,
  aiUsage: () => [...adminKeys.all, "ai-usage"] as const,
  onboardingFunnel: (weeks: number) =>
    [...adminKeys.all, "onboarding-funnel", weeks] as const,
  aiUsageDaily: (days: number) =>
    [...adminKeys.all, "ai-usage-daily", days] as const,
  acquisition: (days: number) =>
    [...adminKeys.all, "acquisition", days] as const,
  acquisitionUsers: (params: {
    dimension: string;
    source: string;
    days: number;
  }) => [...adminKeys.all, "acquisition-users", params] as const,
  referrals: () => [...adminKeys.all, "referrals"] as const,
  distributedBySpark: () => [...adminKeys.all, "distributed-by-spark"] as const,
  audit: () => [...adminKeys.all, "audit"] as const,
  auditList: (params?: {
    date?: string;
    action?: string;
    actorId?: string;
    actorEmail?: string;
    cursor?: string;
  }) => [...adminKeys.audit(), "list", params] as const,
  reports: () => [...adminKeys.all, "reports"] as const,
  helpQuestions: () => [...adminKeys.all, "help-questions"] as const,
  helpQuestionList: (filters: HelpQuestionFilters) =>
    [...adminKeys.helpQuestions(), "list", filters] as const,
  reportList: (filters: AdminReportFilters) =>
    [...adminKeys.reports(), "list", filters] as const,
  alertRules: () => [...adminKeys.all, "alert-rules"] as const,
  feedback: () => [...adminKeys.all, "feedback"] as const,
  feedbackList: (params?: {
    type?: string;
    status?: string;
    cursor?: string;
  }) => [...adminKeys.feedback(), "list", params] as const,
  feedbackDetail: (id: string) =>
    [...adminKeys.feedback(), "detail", id] as const,
  logs: () => [...adminKeys.all, "logs"] as const,
  logGroups: () => [...adminKeys.logs(), "groups"] as const,
  logEvents: (params?: {
    logGroup: string;
    startTime?: number;
    endTime?: number;
    filter?: string;
  }) => [...adminKeys.logs(), "events", params] as const,
};

// ── Queries ────────────────────────────────────────────────

/**
 * Admin queries use a 5-minute staleTime to match the backend's
 * in-memory cache TTL. This prevents redundant Lambda invocations
 * when navigating between admin pages.
 */
const ADMIN_STALE_TIME = 5 * 60_000;

/**
 * How often the dashboard refetches on its own, so a page left open keeps
 * showing current numbers without a manual refresh. Matches the backend's
 * own 5-minute cache TTL — polling faster would just re-read the same cache.
 */
const DASHBOARD_REFETCH_INTERVAL = 5 * 60_000;

export function useAdminDashboard() {
  return useQuery({
    queryKey: adminKeys.dashboard(),
    queryFn: () => adminApi.getDashboard(),
    staleTime: ADMIN_STALE_TIME,
    refetchInterval: DASHBOARD_REFETCH_INTERVAL,
    // Keep refreshing even while the tab is in the background — an admin
    // watching a second monitor should not come back to stale figures.
    refetchIntervalInBackground: true,
  });
}

export function useAdminMetrics(period: string, metric: string) {
  return useQuery({
    queryKey: adminKeys.metrics(period, metric),
    queryFn: () => adminApi.getMetrics({ period, metric }),
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useAdminUsers(params?: {
  search?: string;
  plan?: string;
  status?: "active" | "disabled";
  createdFrom?: string;
  createdTo?: string;
  cursor?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: adminKeys.userList(params),
    queryFn: () => adminApi.listUsers(params),
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useAdminUser(userId: string) {
  return useQuery({
    queryKey: adminKeys.userDetail(userId),
    queryFn: () => adminApi.getUser(userId),
    enabled: !!userId,
    staleTime: ADMIN_STALE_TIME,
  });
}

/** A user's bought/gifted balance and the ledger behind it. */
export function useAdminUserCredits(userId: string) {
  return useQuery({
    queryKey: adminKeys.userCredits(userId),
    queryFn: () => adminApi.getUserCredits(userId),
    enabled: !!userId,
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useGiftCredits() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      body,
    }: {
      userId: string;
      body: GiftCreditsRequest;
    }) => adminApi.giftCredits(userId, body),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.userCredits(userId),
      });
      queryClient.invalidateQueries({ queryKey: adminKeys.userDetail(userId) });
    },
  });
}

export function useAdminContent(params?: {
  type?: string;
  q?: string;
  userId?: string;
  status?: string;
  createdFrom?: string;
  createdTo?: string;
  cursor?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: adminKeys.contentList(params),
    queryFn: () => adminApi.listContent(params),
    staleTime: ADMIN_STALE_TIME,
  });
}

/** One content item's full detail — the id alone cannot locate it; see adminApi.getContent. */
export function useAdminContentDetail(
  contentId: string | undefined,
  params: { userId: string; type?: "source" | "draft" } | undefined
) {
  return useQuery({
    queryKey: adminKeys.contentDetail(contentId ?? "", params),
    queryFn: () => adminApi.getContent(contentId!, params!),
    enabled: Boolean(contentId && params?.userId),
    staleTime: ADMIN_STALE_TIME,
    // Every fetch of a detail is an audited open; a refocus must not write
    // another row.
    refetchOnWindowFocus: false,
  });
}

/**
 * Open a draft or artifact body with a reason. The response is the detail as
 * the server now returns it, so the cache is set from it directly instead of
 * refetching (which would also write another audit row).
 */
export function useRevealAdminContent(contentId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AdminRevealRequest) =>
      adminApi.revealContent(contentId!, data),
    onSuccess: (detail: AdminContentDetail, variables) => {
      queryClient.setQueryData(
        adminKeys.contentDetail(contentId ?? "", {
          userId: variables.userId,
          type: variables.type,
        }),
        detail
      );
    },
  });
}

export function useAdminAIUsage() {
  return useQuery({
    queryKey: adminKeys.aiUsage(),
    queryFn: () => adminApi.getAIUsage(),
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useAdminOnboardingFunnel(weeks: number) {
  return useQuery({
    queryKey: adminKeys.onboardingFunnel(weeks),
    queryFn: () => adminApi.getOnboardingFunnel(weeks),
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useAdminAIUsageDaily(days: AIUsageDailyWindow) {
  return useQuery({
    queryKey: adminKeys.aiUsageDaily(days),
    queryFn: () => adminApi.getAIUsageDaily(days),
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useAdminBillingSummary() {
  return useQuery({
    queryKey: [...adminKeys.all, "billing", "summary"] as const,
    queryFn: () => adminApi.getBillingSummary(),
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useOverridePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PlanOverrideRequest) => adminApi.overridePlan(data),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({
        queryKey: [...adminKeys.all, "billing"],
      });
      queryClient.invalidateQueries({ queryKey: adminKeys.userDetail(userId) });
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useSimulatePayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (event: SimulatorEvent) => adminApi.simulatePayment(event),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...adminKeys.all, "billing"],
      });
    },
  });
}

export function useAdminAcquisition(days: AcquisitionWindow) {
  return useQuery({
    queryKey: adminKeys.acquisition(days),
    queryFn: () => adminApi.getAcquisition(days),
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useAdminReferrals() {
  return useQuery({
    queryKey: adminKeys.referrals(),
    queryFn: () => adminApi.getReferrals(),
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useAdminDistributedBySpark() {
  return useQuery({
    queryKey: adminKeys.distributedBySpark(),
    queryFn: () => adminApi.getDistributedBySpark(),
    staleTime: ADMIN_STALE_TIME,
  });
}

/** Drill-down: the users behind one source cell. `enabled` controls when it fires — see AdminAcquisitionPage.tsx. */
export function useAdminAcquisitionUsers(
  params: {
    dimension: "software" | "selfReported";
    source: string;
    days: AcquisitionWindow;
  } | null
) {
  return useQuery({
    queryKey: adminKeys.acquisitionUsers(
      params ?? { dimension: "software", source: "", days: 30 }
    ),
    queryFn: () => adminApi.getAcquisitionUsers(params!),
    enabled: params !== null,
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useAdminAuditLogs(params?: {
  date?: string;
  cursor?: string;
  limit?: number;
  action?: string;
  actorId?: string;
  actorEmail?: string;
}) {
  return useQuery({
    queryKey: adminKeys.auditList(params),
    queryFn: () => adminApi.getAuditLogs(params),
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useAdminFeedback(params?: {
  type?: string;
  status?: string;
  cursor?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: adminKeys.feedbackList(params),
    queryFn: () => adminApi.listFeedback(params),
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useAdminReports(filters: AdminReportFilters) {
  return useQuery({
    queryKey: adminKeys.reportList(filters),
    queryFn: () => adminApi.listReports(filters),
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useUpdateAdminReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      reportId,
      data,
    }: {
      reportId: string;
      data: { status?: ReportStatus; adminNote?: string };
    }) => adminApi.updateReport(reportId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.reports() });
    },
  });
}

export function useAdminHelpQuestions(filters: HelpQuestionFilters) {
  return useQuery({
    queryKey: adminKeys.helpQuestionList(filters),
    queryFn: () => adminApi.listHelpQuestions(filters),
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useSetHelpQuestionStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      ids,
      status,
    }: {
      ids: string[];
      status: HelpQuestionStatus;
    }) => adminApi.setHelpQuestionStatus(ids, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.helpQuestions() });
    },
  });
}

export function useAlertRules() {
  return useQuery({
    queryKey: adminKeys.alertRules(),
    queryFn: () => adminApi.listAlertRules(),
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useCreateAlertRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AlertRuleInput) => adminApi.createAlertRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.alertRules() });
    },
  });
}

export function useUpdateAlertRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      ruleId,
      data,
    }: {
      ruleId: string;
      data: Partial<AlertRuleInput>;
    }) => adminApi.updateAlertRule(ruleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.alertRules() });
    },
  });
}

export function useDeleteAlertRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) => adminApi.deleteAlertRule(ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.alertRules() });
    },
  });
}

export function useAdminFeedbackDetail(feedbackId: string) {
  return useQuery({
    queryKey: adminKeys.feedbackDetail(feedbackId),
    queryFn: () => adminApi.getFeedback(feedbackId),
    enabled: !!feedbackId,
    staleTime: ADMIN_STALE_TIME,
  });
}

// ── CloudWatch Logs queries ────────────────────────────────

export function useAdminLogGroups() {
  return useQuery({
    queryKey: adminKeys.logGroups(),
    queryFn: () => adminApi.getLogGroups(),
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useAdminLogEvents(
  params: {
    logGroup: string;
    startTime?: number;
    endTime?: number;
    filter?: string;
    limit?: number;
  },
  options?: { refetchInterval?: number | false }
) {
  return useQuery({
    queryKey: adminKeys.logEvents(params),
    queryFn: () => adminApi.getLogEvents(params),
    enabled: !!params.logGroup,
    staleTime: 0, // Always refetch for live tail
    refetchInterval: options?.refetchInterval ?? false,
  });
}

// ── Mutations ──────────────────────────────────────────────

export function useUpdateAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: AdminUserUpdate }) =>
      adminApi.updateUser(userId, data),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.userDetail(userId) });
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useDisableAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, disabled }: { userId: string; disabled: boolean }) =>
      adminApi.disableUser(userId, disabled),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.userDetail(userId) });
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useResetAdminPassword() {
  return useMutation({
    mutationFn: (userId: string) => adminApi.resetPassword(userId),
  });
}

export function useUpdateAdminUserRoles() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: string;
      data: AdminUserRolesUpdate;
    }) => adminApi.updateUserRoles(userId, data),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.userDetail(userId) });
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useRecalculateStreaks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => adminApi.recalculateStreaks(userId),
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.userDetail(userId) });
    },
  });
}

export function useUpdateAdminFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      feedbackId,
      data,
    }: {
      feedbackId: string;
      data: { status?: string; priority?: string; adminNotes?: string };
    }) => adminApi.updateFeedback(feedbackId, data),
    onSuccess: (_, { feedbackId }) => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.feedbackDetail(feedbackId),
      });
      queryClient.invalidateQueries({ queryKey: adminKeys.feedback() });
    },
  });
}

export function useDeleteAdminFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (feedbackId: string) => adminApi.deleteFeedback(feedbackId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.feedback() });
    },
  });
}
